import localStorageService from "../auth/local-storage-service";
import { clearSessionAndRedirectToLogin, refreshAccessToken } from "../auth/token-refresh";
import { KMainFunctions } from "../shared/utility/main-functions";
import { DESIGNER_LIVE_CLIENT_ID, DESIGNER_LIVE_CLIENT_ID_HEADER } from "./designer-live/designer-live-client-id";

declare let window: Window & { kuika: any; userInfo: any };

export interface StreamRequestOptions extends RequestInit {
  requiresAuth?: boolean;
  retryOnAuthFailure?: boolean;
}

/**
 * A stream that never opened because the server answered with an error status. The status
 * has to travel with the error: callers act on it (a collab stream answering 404/403 means
 * the session is gone and must be re-established, not merely reconnected to — KOA-18256),
 * and it used to be legible only by parsing the message text.
 */
export class StreamHttpError extends Error {
  public readonly status: number;

  public constructor(status: number) {
    super(`Stream failed: ${status}`);
    this.name = "StreamHttpError";
    this.status = status;
  }
}

export class StreamingHelperService {
  /**
   * Get authentication headers similar to axios interceptors
   */
  private static getAuthHeaders(): Record<string, string> {
    const urlItems = document.location.pathname.substring(1).split(/\//);
    let domainName = urlItems[0];

    if (
      domainName.toLowerCase() == "signup" ||
      domainName.toLowerCase() == "login" ||
      domainName.toLowerCase() == undefined
    ) {
      domainName = "";
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    // Read token from the same source the axios request interceptor uses
    // (auth/interceptors.ts). Axios.defaults.headers.common.Authorization is
    // only populated after a refresh, so it can be undefined on first load.
    const token = localStorageService.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add domainName header
    if (domainName) {
      headers.domainName = domainName;
    }

    // Add currentProjectId if available
    const activeProjectId = window?.kuika?.activeProjectId;
    if (activeProjectId) {
      headers.currentProjectId = activeProjectId;
    }

    // Add x-clientid if available
    const clientId = window?.userInfo?.id;
    if (clientId) {
      headers["x-clientid"] = clientId;
    }

    // KOA-18196: per-TAB designer-live origin id. This builder serves every raw-fetch
    // path (streamRequest, authenticatedFetch, the token-refresh retry) — without it a
    // streamed mutation (workflow-screen wizard save) published with a NULL origin, the
    // hub suppressed nobody, and the saving tab toasted its own change back at itself.
    headers[DESIGNER_LIVE_CLIENT_ID_HEADER] = DESIGNER_LIVE_CLIENT_ID;

    return headers;
  }

  /**
   * Handle token refresh and retry the request.
   *
   * The refresh itself is delegated to auth/token-refresh so it shares one in-flight
   * request with the axios interceptor: this path used to POST /auth/refreshtoken on
   * its own, and because the backend rotates refresh tokens, a stream 401 racing an
   * axios 401 retired the other's token and logged the user out mid-work.
   */
  private static async handleTokenRefresh(
    originalUrl: string,
    originalOptions: StreamRequestOptions
  ): Promise<Response> {
    try {
      await refreshAccessToken();
    } catch {
      clearSessionAndRedirectToLogin();
      throw new Error("Authentication failed. Please log in again.");
    }

    // Retry with the refreshed Authorization header.
    const retryOptions: StreamRequestOptions = {
      ...originalOptions,
      headers: {
        ...originalOptions.headers,
        ...StreamingHelperService.getAuthHeaders()
      }
    };

    return await fetch(originalUrl, retryOptions);
  }

  /**
   * Process streaming response and extract final result
   */
  private static async processStreamingResponse<T>(
    response: Response,
    onProgress?: (progress: any) => void
  ): Promise<T> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Response body reader is not available");
    }

    const decoder = new TextDecoder();
    let finalResult: T | null = null;
    let lineBuffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunkText = decoder.decode(value, { stream: true });
      lineBuffer += chunkText;

      const completeLines = lineBuffer.split(/\r?\n/);
      lineBuffer = completeLines.pop() ?? "";

      for (const rawLine of completeLines) {
        const line = rawLine.trimEnd();
        const dataMatch = /^data:\s*(.*)$/.exec(line);
        if (!dataMatch) {
          continue;
        }

        const dataStr = dataMatch[1];
        try {
          const progressData = JSON.parse(dataStr);
          onProgress?.(progressData);

          if (progressData?.type === "completed" || progressData?.status === "completed") {
            finalResult = progressData.finalResponse as T;
          }
        } catch (e) {
          console.warn("Failed to parse progress data:", line);
        }
      }
    }

    // Process any remaining buffered line (in case stream closed without trailing newline)
    const trailing = lineBuffer.trim();
    if (trailing) {
      const dataMatch = /^data:\s*(.*)$/.exec(trailing);
      if (dataMatch) {
        const dataStr = dataMatch[1];
        try {
          const progressData = JSON.parse(dataStr);
          onProgress?.(progressData);
          if (progressData?.type === "completed" || progressData?.status === "completed") {
            finalResult = progressData.finalResponse as T;
          }
        } catch (e) {
          console.warn("Failed to parse trailing progress data:", trailing);
        }
      }
    }

    if (finalResult === null) {
      throw new Error("No final result received from streaming endpoint");
    }

    return finalResult;
  }

  /**
   * Make a streaming request with automatic auth handling
   */
  public static async streamRequest<T>(
    url: string,
    options: StreamRequestOptions = {},
    onProgress?: (progress: any) => void
  ): Promise<T> {
    try {
      const { requiresAuth = true, retryOnAuthFailure = true, headers = {}, ...restOptions } = options;

      // Add auth headers if required
      let requestHeaders = { ...headers };
      if (requiresAuth) {
        requestHeaders = {
          ...requestHeaders,
          ...StreamingHelperService.getAuthHeaders()
        };
      }

      const requestOptions: RequestInit = {
        ...restOptions,
        headers: requestHeaders
      };

      let response = await fetch(url, requestOptions);

      // Handle 401 with token refresh
      if (response.status === 401 && retryOnAuthFailure) {
        response = await StreamingHelperService.handleTokenRefresh(url, requestOptions);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await StreamingHelperService.processStreamingResponse<T>(response, onProgress);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  /**
   * Consume a long-lived SSE stream (no terminal "final result"), invoking onEvent
   * for every `data:` line until the stream ends or the signal aborts. Sends the
   * same Bearer auth headers as the other helpers and refreshes once on a 401. The
   * caller is responsible for reconnecting after this resolves/throws.
   */
  public static async streamEvents(url: string, onEvent: (data: unknown) => void, signal: AbortSignal): Promise<void> {
    const requestOptions: StreamRequestOptions = {
      method: "GET",
      headers: { ...StreamingHelperService.getAuthHeaders(), Accept: "text/event-stream" },
      signal
    };

    let response: Response = await fetch(url, requestOptions);
    if (response.status === 401) {
      response = await StreamingHelperService.handleTokenRefresh(url, requestOptions);
    }
    if (!response.ok || !response.body) {
      throw new StreamHttpError(response.status);
    }

    const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
    const decoder: TextDecoder = new TextDecoder();
    let lineBuffer: string = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      lineBuffer += decoder.decode(value, { stream: true });
      const lines: string[] = lineBuffer.split(/\r?\n/);
      lineBuffer = lines.pop() ?? "";
      for (const rawLine of lines) {
        const match: RegExpExecArray | null = /^data:\s*(.*)$/.exec(rawLine.trimEnd());
        if (!match) continue; // ignore `:` heartbeat comments and blank lines
        try {
          onEvent(JSON.parse(match[1]));
        } catch {
          // ignore a malformed line and keep streaming
        }
      }
    }
  }

  /**
   * Make a regular authenticated fetch request (non-streaming)
   */
  public static async authenticatedFetch(url: string, options: StreamRequestOptions = {}): Promise<Response> {
    const { requiresAuth = true, retryOnAuthFailure = true, headers = {}, ...restOptions } = options;

    // Add auth headers if required
    let requestHeaders = { ...headers };
    if (requiresAuth) {
      requestHeaders = {
        ...requestHeaders,
        ...StreamingHelperService.getAuthHeaders()
      };
    }

    const requestOptions: RequestInit = {
      ...restOptions,
      headers: requestHeaders
    };

    let response = await fetch(url, requestOptions);

    // Handle 401 with token refresh
    if (response.status === 401 && retryOnAuthFailure) {
      response = await StreamingHelperService.handleTokenRefresh(url, requestOptions);
    }

    return response;
  }
}
